import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../Command';
import { Icon } from '../Icon';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { SkeletonSpinner } from '../SkeletonLoader';
import { Text } from '../Text';

export type LiveSearchItem = {
  value: string;
  label: string;
  description?: string;
};

export type LiveSearchableDropdownProps<T extends LiveSearchItem = LiveSearchItem> = {
  /** The items to display in the list (consumer provides these, e.g. from React Query). */
  items: T[];
  /** Whether more items are available beyond the current list. Enables the scroll sentinel. */
  hasMore?: boolean;
  /** Whether the initial/search fetch is in progress. Shows spinner in the search input. */
  isLoading?: boolean;
  /** Whether a "load more" fetch is in progress. Shows spinner at the bottom of the list. */
  isLoadingMore?: boolean;
  /** Called on every search input change so the consumer can debounce and refetch. */
  onSearchChange?: (search: string) => void;
  /** Called when the user scrolls to the bottom of the list and `hasMore` is true. */
  onLoadMore?: () => void;
  /** Called when the user selects an item. */
  onSelect?: (item: T) => void;
  /** The currently selected value (controlled). */
  value?: string;
  /** Placeholder shown in the trigger when no item is selected. */
  placeholder?: string;
  /** Placeholder for the search input inside the dropdown. */
  searchPlaceholder?: string;
  /** Text shown when no results match the search. */
  emptyText?: string;
  /** Text shown while results are loading and the list is empty. */
  loadingText?: string;
  /** Additional class names for the trigger button. */
  triggerClassName?: string;
  /** Additional class names for the popover content. */
  className?: string;
  /** Custom render function for each item in the list. */
  renderItem?: (item: T, isSelected: boolean) => React.ReactNode;
  /** Custom render function for the trigger element. */
  renderTrigger?: (params: { selectedItem: T | null; isOpen: boolean }) => React.ReactNode;
  /** Whether the dropdown is disabled. */
  disabled?: boolean;
};

type PopoverSide = 'top' | 'bottom' | null;

function LiveSearchableDropdown<T extends LiveSearchItem = LiveSearchItem>({
  items,
  hasMore = false,
  isLoading = false,
  isLoadingMore = false,
  onSearchChange,
  onLoadMore,
  onSelect,
  value,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  loadingText = 'Loading...',
  triggerClassName,
  className,
  renderItem,
  renderTrigger,
  disabled = false,
}: LiveSearchableDropdownProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [selectedItem, setSelectedItem] = React.useState<T | null>(null);

  const [side, setSide] = React.useState<PopoverSide>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const sideObserverRef = React.useRef<MutationObserver | null>(null);
  const sentinelIoRef = React.useRef<IntersectionObserver | null>(null);
  const scrollContainerRef = React.useRef<HTMLElement | null>(null);

  const effectiveSide = side ?? 'bottom';

  const contentRefCallback = React.useCallback((el: HTMLDivElement | null) => {
    contentRef.current = el;
    if (sideObserverRef.current) {
      sideObserverRef.current.disconnect();
      sideObserverRef.current = null;
    }
    setSide(null);
    if (el) {
      const applySide = () => {
        const val = el.getAttribute('data-side');
        if (val === 'top' || val === 'bottom') setSide(val);
      };
      applySide();
      const obs = new MutationObserver(applySide);
      obs.observe(el, { attributes: true, attributeFilter: ['data-side'] });
      sideObserverRef.current = obs;
    }
  }, []);

  // Reset search when popover closes
  React.useEffect(() => {
    if (!open) {
      setSearch('');
      onSearchChange?.('');
    }
  }, [open, onSearchChange]);

  // Stable refs for values the IntersectionObserver callback reads
  const loadMoreStateRef = React.useRef({ hasMore, isLoading, isLoadingMore, onLoadMore });
  loadMoreStateRef.current = { hasMore, isLoading, isLoadingMore, onLoadMore };

  // Callback ref for the sentinel — sets up / tears down the IntersectionObserver
  // exactly when the element mounts or unmounts, avoiding timing issues.
  const sentinelRefCallback = React.useCallback(
    (el: HTMLDivElement | null) => {
      if (sentinelIoRef.current) {
        sentinelIoRef.current.disconnect();
        sentinelIoRef.current = null;
      }
      if (!el) return;

      const io = new IntersectionObserver(
        (entries) => {
          const { hasMore: hm, isLoading: il, isLoadingMore: ilm, onLoadMore: olm } = loadMoreStateRef.current;
          if (entries[0]?.isIntersecting && hm && !il && !ilm) {
            olm?.();
          }
        },
        { root: scrollContainerRef.current, threshold: 0.1 },
      );
      io.observe(el);
      sentinelIoRef.current = io;
    },
    [], // stable — reads mutable refs
  );

  // Sync selectedItem when controlled `value` changes
  React.useEffect(() => {
    if (value === undefined) return;
    if (value === '') {
      setSelectedItem(null);
      return;
    }
    setSelectedItem((prev) => {
      // The value is on the loaded page: select it.
      const match = items.find((item) => item.value === value);
      if (match) return match;

      // Not on the page, but already selected (eg. picked from search before the list reset): keep it.
      if (prev?.value === value) return prev;

      // Not on the page and the selection is for a different value: clear it, so the trigger falls back to the store's name.
      return null;
    });
  }, [value, items]);

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    onSearchChange?.(newSearch);
  };

  const handleSelect = (selectedValue: string) => {
    const item = items.find((i) => i.value === selectedValue);
    if (!item) return;
    setSelectedItem(item);
    onSelect?.(item);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild disabled={disabled}>
        {renderTrigger ? (
          renderTrigger({ selectedItem, isOpen: open })
        ) : (
          <button
            role="combobox"
            aria-expanded={open}
            type="button"
            className={cn(
              'h-7 w-full min-w-0 rounded border bg-white px-2 text-xs leading-[1.4] text-grey-80 transition-[color,box-shadow,border-color] outline-none',
              'border-grey-30',
              'focus-visible:border-focus',
              'data-[state=open]:border-blue-50',
              effectiveSide === 'bottom' && 'data-[state=open]:rounded-b-none data-[state=open]:rounded-t',
              effectiveSide === 'top' && 'data-[state=open]:rounded-t-none data-[state=open]:rounded-b',
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-grey-10',
              'dark:bg-white/30',
              'flex items-center justify-between gap-2',
              selectedItem ? '' : 'text-grey-50',
              'w-[200px]',
              triggerClassName,
            )}
          >
            <Text className="text-xs truncate">{selectedItem ? selectedItem.label : placeholder}</Text>
            <Icon icon="chevronDown" size={16} className="shrink-0 opacity-50" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        ref={contentRefCallback}
        className={cn(
          'min-w-(--radix-popover-trigger-width) max-w-[300px] p-0 border border-blue-50 bg-white shadow-md overflow-hidden',
          effectiveSide === 'bottom' && 'rounded-t-none rounded-b',
          effectiveSide === 'top' && 'rounded-t rounded-b-none',
          className,
        )}
        align="start"
        sideOffset={-1}
      >
        <Command
          className={cn('min-w-full flex flex-col', effectiveSide === 'bottom' ? 'rounded-b' : 'rounded-t')}
          shouldFilter={false}
        >
          <div className={cn('relative', effectiveSide === 'top' ? 'order-2' : undefined)}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={handleSearchChange}
              borderColor="border-blue-50"
              className={isLoading ? 'pr-7' : undefined}
              wrapperClassName={cn(
                effectiveSide === 'bottom' && 'rounded-t-none border-t-0',
                effectiveSide === 'top' && '!border-b-0 border-t border-blue-50 rounded-b-none',
              )}
            />
            {isLoading && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <SkeletonSpinner size={14} />
              </div>
            )}
          </div>
          <div
            ref={(el) => {
              // Capture the CommandList (the actual scrollable element) inside this wrapper
              scrollContainerRef.current = el?.querySelector('[data-slot="command-list"]') ?? null;
            }}
            className={cn('min-h-0 flex-1 overflow-hidden', effectiveSide === 'top' && 'order-1')}
          >
            <CommandList>
              {isLoading && items.length === 0 && (
                <div className="py-6 text-center text-xs text-grey-50">{loadingText}</div>
              )}

              {!isLoading && items.length === 0 && (
                <CommandEmpty>
                  <Text className="text-xs">{emptyText}</Text>
                </CommandEmpty>
              )}

              {items.length > 0 && (
                <CommandGroup>
                  {items.map((item) => {
                    const isSelected = item.value === value;
                    return (
                      <CommandItem
                        key={item.value}
                        value={item.value}
                        onSelect={handleSelect}
                        className="overflow-hidden"
                      >
                        {renderItem ? (
                          renderItem(item, isSelected)
                        ) : (
                          <>
                            <Text className="text-xs truncate flex-1 text-left">{item.label}</Text>
                            {item.description && (
                              <Text className="text-xs text-grey-50 truncate">{item.description}</Text>
                            )}
                            <Icon
                              icon="check"
                              size={16}
                              className={cn('ml-auto shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                            />
                          </>
                        )}
                      </CommandItem>
                    );
                  })}

                  {hasMore && <div ref={sentinelRefCallback} className="h-1" aria-hidden="true" />}

                  {isLoadingMore && (
                    <div className="flex items-center justify-center py-2 gap-2">
                      <SkeletonSpinner size={14} />
                      <Text className="text-xs text-grey-50">Loading more...</Text>
                    </div>
                  )}
                </CommandGroup>
              )}
            </CommandList>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { LiveSearchableDropdown };
